import { alignerMonitoringState, type MonitoringMeta } from './alignerMonitoringState';
import { Tooltip } from './Tooltip';

interface AlignerMonitoringCellProps {
    current: number | null;
    total: number | null;
    monitoringStatus: string | null;
}

// Matches the doctor dashboard's AlignerProgress ring (size 24 viewBox, 2px
// stroke). Rendered at ~42px.
const ALIGNER_PROGRESS_SIZE = 24;
const ALIGNER_PROGRESS_STROKE = 2;

interface StateGaugeProps {
    current: number;
    total: number;
    meta: MonitoringMeta;
    monitoring: boolean;
}

function StateGauge({ current, total, meta, monitoring }: StateGaugeProps) {
    const radius = Math.max((ALIGNER_PROGRESS_SIZE - ALIGNER_PROGRESS_STROKE) / 2, 0);
    const circumference = 2 * Math.PI * radius;
    const ratio = monitoring && total > 0 ? Math.min(1, current / total) : 0;
    const dashoffset = circumference * (1 - ratio);
    const { Icon, iconColor, arcColor } = meta;

    return (
        <div className="relative flex items-center justify-center w-[42px] h-[42px] flex-shrink-0">
            <svg
                className="h-full w-full"
                viewBox={`0 0 ${ALIGNER_PROGRESS_SIZE} ${ALIGNER_PROGRESS_SIZE}`}
                aria-hidden="true"
            >
                {/* Track */}
                <circle
                    cx={ALIGNER_PROGRESS_SIZE / 2}
                    cy={ALIGNER_PROGRESS_SIZE / 2}
                    r={radius}
                    strokeWidth={ALIGNER_PROGRESS_STROKE}
                    className="text-gray-200"
                    stroke="currentColor"
                    fill="transparent"
                />
                {/* Progress arc — only when monitoring is active/paused/stopped. */}
                {monitoring && (
                    <circle
                        cx={ALIGNER_PROGRESS_SIZE / 2}
                        cy={ALIGNER_PROGRESS_SIZE / 2}
                        r={radius}
                        strokeWidth={ALIGNER_PROGRESS_STROKE}
                        className={`${arcColor} transition-[stroke-dashoffset] duration-300`}
                        strokeDasharray={circumference}
                        strokeDashoffset={dashoffset}
                        strokeLinecap="round"
                        stroke="currentColor"
                        fill="transparent"
                        transform={`rotate(-90 ${ALIGNER_PROGRESS_SIZE / 2} ${ALIGNER_PROGRESS_SIZE / 2})`}
                    />
                )}
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
                {monitoring ? (
                    <>
                        <span className="text-[11px] font-semibold text-gray-700">
                            {current}/{total}
                        </span>
                        <Icon className={`w-2.5 h-2.5 ${iconColor}`} />
                    </>
                ) : (
                    <Icon className={`w-4 h-4 ${iconColor}`} />
                )}
            </div>
        </div>
    );
}

/**
 * Treatment-in-progress column. Active / paused / stopped monitoring shows an
 * aligner progress gauge (current / total) with the monitoring-state logo inside
 * the ring. "Not started" shows the same ring, empty, with the muted not-started
 * icon inside.
 *
 * Shared between the admin work-queue (files-viewer) and the doctor dashboard
 * patients table.
 */
export function AlignerMonitoringCell({
    current,
    total,
    monitoringStatus
}: AlignerMonitoringCellProps) {
    const state = alignerMonitoringState(current, total, monitoringStatus);

    return (
        <Tooltip content={state.tooltip} side="top">
            <div>
                <StateGauge
                    current={state.current}
                    total={state.total}
                    meta={state.meta}
                    monitoring={state.isMonitoring}
                />
            </div>
        </Tooltip>
    );
}
